import { isEmpty } from 'lodash';
import { isLayoutComponent, isLayoutSection } from '@shared/scenes/workflows/utils';
import {
  AnyLayoutItem,
  hasLayoutContent,
  isLayoutContentItem,
} from '@shared/scenes/workflows/viewTemplateLayoutItems';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';

/**
 * How a stored view template layout meets the workflow template's components as they are now.
 *
 * One set of rules for every surface that reads a layout — card forms, subforms, collection views
 * and the builder — so a field never sits in one place in the builder and another on the page.
 */

export interface ReconciledLayout {
  /** The layout's own items, checked against the components that exist now. */
  items: AnyLayoutItem[];
  /** The components no item places, in workflow template order. */
  unplacedComponents: API.AnyComponent[];
}

/**
 * A stored layout's items checked against the components that exist now.
 *
 * - A reference to a component the workflow template no longer has is dropped.
 * - Each component is placed once. A section claims its components ahead of a standalone item
 *   naming the same one, wherever that item sits; between two sections, the first one wins.
 * - A section is kept even when nothing is left in it, because the builder lets one be empty.
 */
export function reconcileLayoutItems(
  components: readonly API.AnyComponent[],
  items: readonly AnyLayoutItem[],
): ReconciledLayout {
  const existingIds = new Set(components.map(component => component.id));
  const placedIds = new Set<string>();

  const place = (componentId: string | null | undefined): componentId is string => {
    if (!componentId || !existingIds.has(componentId) || placedIds.has(componentId)) {
      return false;
    }
    placedIds.add(componentId);
    return true;
  };

  // Sections go first so they keep a component that a standalone item also names.
  const sectionComponentIds = new Map(
    items
      .filter(isLayoutSection)
      .map(section => [section, (section.componentIds ?? []).filter(place)]),
  );

  const reconciledItems = items.flatMap((item): AnyLayoutItem[] => {
    if (isLayoutSection(item)) {
      return [{ ...item, componentIds: sectionComponentIds.get(item) ?? [] }];
    }
    if (isLayoutComponent(item)) {
      return place(item.componentId) ? [item] : [];
    }
    return [item];
  });

  return {
    items: reconciledItems,
    unplacedComponents: components.filter(component => !placedIds.has(component.id)),
  };
}

export interface ResolveLayoutOptions {
  /**
   * Which of the components the layout never places get appended. All of them by default; a card
   * form leaves out the ones it never renders as a field of their own.
   */
  shouldAppend?: (component: API.AnyComponent) => boolean;
}

/**
 * The layout as a surface renders it.
 *
 * The items are reconciled (see `reconcileLayoutItems`), then sections with nothing in them and
 * text and media items with nothing to show are dropped. Every component the layout never places
 * follows as a standalone item, in workflow template order, so a field added since the layout was
 * saved still shows up.
 */
export function resolveLayoutItems(
  components: readonly API.AnyComponent[],
  layout: API.ViewTemplateLayoutFragment | null | undefined,
  { shouldAppend = () => true }: ResolveLayoutOptions = {},
): AnyLayoutItem[] {
  const { items, unplacedComponents } = reconcileLayoutItems(components, layout?.items ?? []);

  const renderedItems = items.filter(item => {
    if (isLayoutSection(item)) {
      return !isEmpty(item.componentIds);
    }
    if (isLayoutContentItem(item)) {
      return hasLayoutContent(item);
    }
    return isLayoutComponent(item);
  });

  return [...renderedItems, ...unplacedComponents.filter(shouldAppend).map(toLayoutComponent)];
}

/**
 * The components in layout order, for a surface that shows neither sections nor text and media:
 * a section's components take its place in the order, and content items are skipped.
 */
export function getComponentsInLayoutOrder(
  components: readonly API.AnyComponent[],
  layout: API.ViewTemplateLayoutFragment | null | undefined,
  options?: ResolveLayoutOptions,
): API.AnyComponent[] {
  const componentsById = new Map(components.map(component => [component.id, component]));

  return resolveLayoutItems(components, layout, options)
    .flatMap(layoutComponentIds)
    .map(componentId => componentsById.get(componentId))
    .filter(notNil);
}

/** The component ids an item places: a section's, a standalone item's own, or none. */
export function layoutComponentIds(item: AnyLayoutItem): string[] {
  if (isLayoutSection(item)) {
    return item.componentIds ?? [];
  }
  if (isLayoutComponent(item) && item.componentId) {
    return [item.componentId];
  }
  return [];
}

function toLayoutComponent(component: API.AnyComponent): API.ViewTemplateLayoutComponentFragment {
  return {
    id: component.id,
    type: 'SINGLE_COMPONENT',
    componentId: component.id,
  };
}
